import React, { ReactNode } from 'react'

import { SxProps } from '@mui/material'

import { useAnalysisType } from 'redux/hooks/AnalysisTypes'
import { useAnalysisTypeAnalyses } from 'redux/hooks/Analyses'
import { useMetric } from 'redux/hooks/Metrics'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'

import IconTip from 'components_new/molecules/IconTip'
import NavigationItem from 'components_new/molecules/NavigationItem'
import SimpleTable from 'components_new/molecules/SimpleTable'

import ChatAvatar from '../../ChatBox/ChatAvatar'
import AnalysisTypeExclusions from '../AnalysisTypeExclusions'
import { getParsedDate } from 'utils/dateParser'
import { getMetricLinkSearch, getMetricTitle } from 'utils/metric'
import { formatDurationFromSeconds } from 'utils/duration'
import { canExclude } from 'utils/analysis'
import { useTranslation } from 'language/translator'

interface AnalysisTypeDescriptionProps {
  /** Where it shows, which the exclusion texts are worded for. */
  context: 'dialog' | 'page'
  id: string
  sx?: SxProps
}

const AnalysisTypeDescription = (props: AnalysisTypeDescriptionProps) => {
  const { context, id, sx } = props

  const { analysisType } = useAnalysisType(id)
  const { lastPublishedAt } = useAnalysisTypeAnalyses(id)
  const metric = useMetric(analysisType?.metric?.id, null)
  const { lang, t, translateEnum } = useTranslation()

  const metricTitle =
    analysisType?.metric && metric
      ? getMetricTitle(
        metric,
        analysisType.metric.settings,
        lang,
        translateEnum
      )
      : ''

  const rows: { cells: ReactNode[] }[] = [
    {
      cells: [
        <Text color="text.secondary" key={0} variant="body2">
          {t('analysis_types', 'analyzed_by_label')}
        </Text>,
        <Box
          key={1}
          sx={{
            alignItems: 'center',
            background:
              'linear-gradient(90deg, rgba(34, 197, 94, 0.12), transparent)',
            borderRadius: 1,
            display: 'flex',
            gap: 0.5,
            pl: 0.5
          }}
        >
          <Box sx={{ height: 24, width: 24 }}>
            <ChatAvatar />
          </Box>
          <Text color="text.primary" fontWeight="bold" variant="body1">
            {t('analysis_types', 'homepal_ai_title')}
          </Text>
        </Box>
      ]
    }
  ]

  if (analysisType?.metric && metric) {
    rows.push({
      cells: [
        <Text color="text.secondary" key={0} variant="body2">
          {t('analysis_types', 'tracked_with_label')}
        </Text>,
        <NavigationItem
          arrow={true}
          border={true}
          color="primary"
          external={true}
          item={{
            href: `/metrics/${analysisType.metric.id}`,
            icon: { name: 'Metric' },
            search: getMetricLinkSearch(analysisType.metric.settings),
            title: metricTitle
          }}
          key={1}
        />
      ]
    })
  }

  rows.push(
    {
      cells: [
        <Text color="text.secondary" key={0} variant="body2">
          {t('analysis_types', 'estimated_in_label')}
        </Text>,
        <Text color="text.primary" key={1} variant="body1">
          {t('analysis_types', 'increased_noi_value')}{' '}
          <Box component="span" sx={{ color: 'text.secondary' }}>
            +kr/{t('global', 'year', true)}
          </Box>
        </Text>
      ]
    },
    {
      cells: [
        <Text color="text.secondary" key={0} variant="body2">
          {t('analysis_types', 'avg_run_time_label')}
        </Text>,
        <Text
          color={
            analysisType?.average_runtime_seconds
              ? 'text.primary'
              : 'text.disabled'
          }
          key={1}
          variant="body1"
        >
          {analysisType?.average_runtime_seconds
            ? formatDurationFromSeconds(analysisType.average_runtime_seconds)
            : t('analysis_types', 'run_time_unavailable')}
        </Text>
      ]
    },
    {
      cells: [
        <Text color="text.secondary" key={0} variant="body2">
          {t('analysis_types', 'last_run_label')}
        </Text>,
        <Text
          color={lastPublishedAt ? 'success.text' : 'text.disabled'}
          key={1}
          variant="body1"
        >
          {lastPublishedAt ? (
            <Icon
              name="Check"
              sx={{
                fontSize: '1em',
                mr: '0.25em',
                verticalAlign: 'middle'
              }}
            />
          ) : null}
          {lastPublishedAt
            ? getParsedDate(lastPublishedAt, lang, { time: 'full' })
            : t('analysis_types', 'no_runs')}
        </Text>
      ]
    }
  )

  const excludable = analysisType ? canExclude(analysisType) : false

  if (excludable) {
    rows.push({
      cells: [
        <Text color="text.secondary" key={0} variant="body2">
          {t('analysis_types', 'excluded_label')}{' '}
          <IconTip
            size="xsmall"
            title={
              <>
                <u>{t('analysis_types', 'excluded_label')}</u>{' '}
                {context === 'dialog'
                  ? t('analysis_types', 'excluded_help_dialog')
                  : t('analysis_types', 'excluded_help_page')}
              </>
            }
          />
        </Text>,
        <AnalysisTypeExclusions context={context} id={id} key={1} />
      ]
    })
  }

  return (
    <SimpleTable rows={rows} sx={{ mb: 2, minHeight: 'fit-content', ...sx }} />
  )
}

export default AnalysisTypeDescription
